'use client';

import {useDevConfig} from 'common/dev';
import {generateSubmissionFlowId} from 'common/submission_flow_id';
import {getUser} from 'common/users';
import {useAuth} from 'iq-auth';
import {enqueuePostAuthAction} from 'iq-auth/post_auth_actions';
import {getAllCompanies} from 'iq-companies/api';
import {
  getTakehomeUploadUrl,
  submitInterviewExperience,
  validateInterviewExperienceStep,
  validateInterviewExperienceSubmitData,
  validateTakehomeFiles,
} from 'iq-interview_experiences/api';
import {OUTCOME_COLOR_OPTIONS, OUTCOME_OPTIONS} from 'iq-interview_experiences/constants';
import {submitInterviewExperienceAction} from 'iq-interview_experiences/post_auth';
import {
  trackSubmissionFlowStepCompleted,
  trackSubmissionFlowStepViewed,
  trackSubmissionFlowSubmitted,
} from 'iq-interview_experiences/tracking';
import type {
  InterviewExperienceSubmitData,
  SubmissionFlowConfig,
  SubmissionFlowData,
  TakeHomeFile,
} from 'iq-interview_experiences/types';
import {SubmissionSource} from 'iq-interview_experiences/types';
import {getPositions} from 'iq-positions/api';
import {useEffect, useState} from 'react';

type UseSubmissionFlowOptions = {
  guideSlug?: string;
  defaultCompanySlug?: string;
  defaultPositionSlug?: string;
  source?: SubmissionSource;
};

type Direction = -1 | 1;

const TOTAL_STEPS = 3;
export const MIN_EXPERIENCE_CHARS = 200;

const STEP_QUESTIONS = {
  experience:
    'Describe your interview experience in detail — what happened, how many rounds, what surprised you, and where did you struggle or feel confident?',
  questions:
    'What specific questions were you asked in the interview? List the technical questions, case prompts, and anything else you can remember.',
} as const;

const DEFAULT_DATA: SubmissionFlowData = {
  company: null,
  position: null,
  customDate: '',
  location: '',
  outcome: null,
  experience: '',
  questions: '',
  takeHomeFiles: [],
};

function resolveConfig(config?: SubmissionFlowConfig) {
  return {
    allowQuestions: config?.allowQuestions ?? true,
    allowTakeHome: config?.allowTakeHome ?? false,
    forceTakeHomeOnly: config?.forceTakeHomeOnly ?? false,
    allowDiaform: config?.allowDiaform ?? false,
    allowStructuredQuestions: config?.allowStructuredQuestions ?? false,
  };
}

const _runValidateTakehome =
  (setValidating: (v: boolean) => void, setValidationError: (e: string | null) => void) =>
  async (files: TakeHomeFile[]) => {
    const uploaded = files.filter((f) => f.s3Key && !f.error);
    if (uploaded.length === 0) return {valid: true, feedback: '', fileCategories: []};
    setValidating(true);
    setValidationError(null);
    try {
      const result = await validateTakehomeFiles<TakeHomeFile['category']>(
        uploaded.map((f) => ({filename: f.name, size: f.size, contentType: '', s3Key: f.s3Key})),
      );
      setValidating(false);
      return result;
    } catch {
      setValidationError('Failed to validate take-home files. Please try again.');
      setValidating(false);
      return null;
    }
  };

function isStep0Valid(data: SubmissionFlowData): boolean {
  return !!(
    data.company?.label.trim() &&
    data.position?.label.trim() &&
    data.customDate &&
    data.outcome
  );
}

function isStep1Valid(data: SubmissionFlowData): boolean {
  return data.experience.trim().length >= MIN_EXPERIENCE_CHARS;
}

function buildSubmitData(
  data: SubmissionFlowData,
  submissionId: string,
  guideSlug: string,
  source?: SubmissionSource,
): InterviewExperienceSubmitData {
  const outcome = data.outcome!;
  return {
    outcome: OUTCOME_OPTIONS.find((o) => o.value === outcome)!.label,
    outcome_color: OUTCOME_COLOR_OPTIONS[outcome],
    position:
      data.position!.value === null
        ? {mode: 'unlisted', name: data.position!.label}
        : {mode: 'listed', slug: data.position!.value},
    date: data.customDate,
    company:
      data.company!.value === null
        ? {mode: 'unlisted', name: data.company!.label}
        : {mode: 'listed', slug: data.company!.value},
    experience: data.experience.trim(),
    questions: data.questions.trim(),
    location: data.location,
    take_home_files: data.takeHomeFiles
      .filter((f) => f.s3Key && !f.error)
      .map((f) => ({filename: f.name, s3_key: f.s3Key, category: f.category})),
    submission_flow_id: submissionId,
    guide_slug: guideSlug,
    ...(source ? {source} : {}),
  };
}

const _runSubmit =
  (
    setSubmitting: (v: boolean) => void,
    setValidationError: (e: string | null) => void,
    setSubmitted: (v: boolean) => void,
  ) =>
  async (submitData: InterviewExperienceSubmitData, submissionId: string, companySlug?: string) => {
    setSubmitting(true);
    try {
      await submitInterviewExperience(submitData);
      void trackSubmissionFlowSubmitted(submissionId, companySlug);
      setSubmitted(true);
    } catch {
      setValidationError('Something went wrong on our end. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

const _runPreValidation =
  (setValidating: (v: boolean) => void, setValidationError: (e: string | null) => void) =>
  async (submitData: InterviewExperienceSubmitData): Promise<{valid: boolean; reason: string}> => {
    setValidating(true);
    setValidationError(null);
    try {
      const result = await validateInterviewExperienceSubmitData(submitData);
      setValidating(false);
      if (!result.success) {
        setValidationError(result.reason);
        return {valid: false, reason: result.reason};
      }
      return {valid: true, reason: ''};
    } catch {
      setValidating(false);
      const reason = 'Unable to validate your submission. Please try again.';
      setValidationError(reason);
      return {valid: false, reason};
    }
  };

const _runValidateStep =
  (setValidating: (v: boolean) => void, setValidationError: (e: string | null) => void) =>
  async (
    stepId: string,
    question: string,
    answer: string,
  ): Promise<{valid: boolean; reason: string}> => {
    setValidating(true);
    setValidationError(null);
    try {
      const result = await validateInterviewExperienceStep(stepId, question, answer);
      setValidating(false);
      if (!result.valid) setValidationError(result.reason);
      return result;
    } catch {
      setValidating(false);
      const reason = 'Unable to validate your answer. Please try again.';
      setValidationError(reason);
      return {valid: false, reason};
    }
  };

export function useSubmissionFlow(
  config?: SubmissionFlowConfig,
  options?: UseSubmissionFlowOptions,
) {
  const {user, setUser} = useAuth();
  const {prefillFlowData, setPrefillFlowData} = useDevConfig();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState<Direction>(1);
  const [transitioning, setTransitioning] = useState(false);
  const [formData, setFormData] = useState<SubmissionFlowData>(DEFAULT_DATA);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const resolvedConfig = resolveConfig(config);
  const [submissionId] = useState(() => generateSubmissionFlowId());
  const runPreValidation = _runPreValidation(setValidating, setValidationError);
  const runValidateStep = _runValidateStep(setValidating, setValidationError);
  const runSubmit = _runSubmit(setSubmitting, setValidationError, setSubmitted);
  const runValidateTakehome = _runValidateTakehome(setValidating, setValidationError);

  useEffect(() => {
    if (step < TOTAL_STEPS) void trackSubmissionFlowStepViewed(step, submissionId);
  }, [step, submissionId]);

  useEffect(() => {
    if (!prefillFlowData) return;
    setFormData((prev) => ({...prev, ...prefillFlowData.data}));
    setStep(prefillFlowData.step);
    setPrefillFlowData(null);
  }, [prefillFlowData]);

  useEffect(() => {
    if (!options?.defaultCompanySlug && !options?.defaultPositionSlug) return;
    void (async () => {
      const [companies, positions] = await Promise.all([
        options?.defaultCompanySlug ? getAllCompanies() : Promise.resolve([]),
        options?.defaultPositionSlug ? getPositions() : Promise.resolve([]),
      ]);
      setFormData((prev) => {
        const company = options?.defaultCompanySlug
          ? companies.find((c) => c.slug === options.defaultCompanySlug)
          : undefined;
        const position = options?.defaultPositionSlug
          ? positions.find((p) => p.slug === options.defaultPositionSlug)
          : undefined;
        return {
          ...prev,
          ...(company ? {company: {value: company.slug, label: company.name}} : {}),
          ...(position ? {position: {value: position.slug, label: position.name}} : {}),
        };
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function goToStep(next: number, dir: Direction) {
    setDirection(dir);
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setValidationError(null);
      setTransitioning(false);
    }, 200);
  }

  function setField<K extends keyof SubmissionFlowData>(key: K, value: SubmissionFlowData[K]) {
    setFormData((prev) => ({...prev, [key]: value}));
  }

  async function addTakehomeFiles(files: File[]) {
    const newEntries: TakeHomeFile[] = files.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name,
      size: f.size,
      s3Key: '',
      category: 'unknown' as const,
      uploading: true,
      error: null,
    }));
    setFormData((prev) => ({...prev, takeHomeFiles: [...prev.takeHomeFiles, ...newEntries]}));

    await Promise.all(
      newEntries.map(async (entry, i) => {
        const file = files[i];
        if (!file) return;
        try {
          const {uploadUrl, s3Key, fields} = await getTakehomeUploadUrl(
            entry.name,
            file.type || 'application/octet-stream',
            submissionId,
          );
          // The backend issues a presigned POST: send the policy fields as a
          // multipart form with the file appended last, not a raw PUT.
          const uploadForm = new FormData();
          for (const [name, value] of Object.entries(fields)) {
            uploadForm.append(name, value);
          }
          uploadForm.append('file', file);
          const uploadResponse = await fetch(uploadUrl, {
            method: 'POST',
            body: uploadForm,
          });
          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
          }
          setFormData((prev) => ({
            ...prev,
            takeHomeFiles: prev.takeHomeFiles.map((f) =>
              f.id === entry.id ? {...f, uploading: false, s3Key} : f,
            ),
          }));
        } catch {
          setFormData((prev) => ({
            ...prev,
            takeHomeFiles: prev.takeHomeFiles.map((f) =>
              f.id === entry.id
                ? {...f, uploading: false, error: 'Upload failed. Please try again.'}
                : f,
            ),
          }));
        }
      }),
    );
  }

  function removeTakehomeFile(id: string) {
    setFormData((prev) => ({
      ...prev,
      takeHomeFiles: prev.takeHomeFiles.filter((f) => f.id !== id),
    }));
  }

  async function handleNext() {
    if (step === 0) {
      if (!isStep0Valid(formData)) {
        setValidationError('Please fill in company, position, date, and outcome.');
        return;
      }
      void trackSubmissionFlowStepCompleted(0, formData, submissionId);
      goToStep(1, 1);
      return;
    }

    if (step === 1) {
      if (!isStep1Valid(formData)) {
        setValidationError(`Please write at least ${MIN_EXPERIENCE_CHARS} characters.`);
        return;
      }
      const stepResult = await runValidateStep(
        'experience',
        STEP_QUESTIONS.experience,
        formData.experience,
      );
      void trackSubmissionFlowStepCompleted(1, formData, submissionId, stepResult);
      if (!stepResult.valid) return;
      goToStep(2, 1);
      return;
    }

    let submitFormData = formData;
    if (resolvedConfig.allowTakeHome && formData.takeHomeFiles.length > 0) {
      const takehomeResult = await runValidateTakehome(formData.takeHomeFiles);
      if (!takehomeResult) return;
      if (!takehomeResult.valid) {
        setValidationError(takehomeResult.feedback);
        return;
      }
      // Compute the categorized list locally: setFormData is async, so the
      // submit payload below must not rely on the state update landing first.
      const categorizedFiles = formData.takeHomeFiles.map((f) => {
        const match = takehomeResult.fileCategories.find((c) => c.filename === f.name);
        return match ? {...f, category: match.category} : f;
      });
      setFormData((prev) => ({...prev, takeHomeFiles: categorizedFiles}));
      submitFormData = {...formData, takeHomeFiles: categorizedFiles};
    }

    const submitData = buildSubmitData(submitFormData, submissionId, options?.guideSlug ?? '', options?.source);

    if (!resolvedConfig.forceTakeHomeOnly) {
      const stepResult = await runPreValidation(submitData);
      void trackSubmissionFlowStepCompleted(2, formData, submissionId, stepResult);
      if (!stepResult.valid) return;
    } else {
      void trackSubmissionFlowStepCompleted(2, formData, submissionId);
    }

    if (!user.loggedIn) {
      // Persist the experience so it's submitted right after auth — inline on
      // the success callback, or in the OAuth callback after a redirect.
      enqueuePostAuthAction(submitInterviewExperienceAction, submitData);
      goToStep(3, 1);
      return;
    }

    await runSubmit(submitData, submissionId, formData.company?.value ?? undefined);
  }

  async function handleAuthSuccess() {
    const raw = localStorage.getItem('iq:pending-experience');
    if (raw) {
      try {
        const data = JSON.parse(raw) as InterviewExperienceSubmitData;
        await submitInterviewExperience(data);
        void trackSubmissionFlowSubmitted(submissionId);
        localStorage.removeItem('iq:pending-experience');
      } catch {
        localStorage.removeItem('iq:pending-experience');
        getUser()
          .then(setUser)
          .catch(() => {});
        setValidationError('Something went wrong on our end. Please try again.');
        goToStep(2, -1);
        return;
      }
    }
    getUser()
      .then(setUser)
      .catch(() => {});
    setSubmitted(true);
  }

  const isAnyFileUploading = formData.takeHomeFiles.some((f) => f.uploading);
  const canProceed =
    step === 0 ? isStep0Valid(formData) : step === 1 ? isStep1Valid(formData) : !isAnyFileUploading;

  return {
    step,
    direction,
    transitioning,
    formData,
    setField,
    validating,
    submitting,
    validationError,
    submitted,
    canProceed,
    goToStep,
    handleNext,
    handleAuthSuccess,
    addTakehomeFiles,
    removeTakehomeFile,
    resolvedConfig,
    totalSteps: TOTAL_STEPS,
    submissionId,
  };
}
